import { NavLink } from 'react-router-dom';
import {
  Activity,
  Award,
  ClipboardList,
  Coins,
  Gift,
  LayoutDashboard,
  Layers,
  Network,
  Power,
  ShieldCheck,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Separator } from '@/components/ui/primitives.jsx';
import { useAuth } from '@/routes/AuthProvider.jsx';
import { APP_NAME } from '@/lib/constants.js';
import { cn } from '@/lib/utils.js';

/**
 * The navigation.
 *
 * `superAdmin: true` entries are **hidden** from a plain admin rather than
 * shown and rejected. The API enforces the boundary regardless — it answers 403
 * whatever the sidebar renders — so this is purely about not offering somebody
 * a page that cannot load. Clicking into a permission error is a worse
 * experience than never seeing the link.
 *
 * The groups follow the shape of the domain rather than the shape of the API:
 * "Money" gathers stakes, withdrawals and income because that is one job, even
 * though they are three route groups.
 */
const SECTIONS = [
  {
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    label: 'Accounts',
    items: [{ to: '/users', label: 'Users', icon: Users }],
  },
  {
    label: 'Money',
    items: [
      { to: '/stakes', label: 'Stakes', icon: Coins },
      { to: '/withdrawals', label: 'Withdrawals', icon: Wallet },
      { to: '/income', label: 'Income', icon: TrendingUp },
    ],
  },
  {
    label: 'Rewards',
    items: [
      { to: '/offers', label: 'Offer claims', icon: Gift },
      { to: '/registration-rewards', label: 'Registration rewards', icon: Award },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/packages', label: 'Packages', icon: Layers },
      { to: '/ranks', label: 'Ranks', icon: Trophy },
      { to: '/upline-levels', label: 'Upline levels', icon: Network },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/audit-log', label: 'Audit log', icon: ClipboardList, superAdmin: true },
      { to: '/system', label: 'System', icon: Activity },
      { to: '/controls', label: 'Controls', icon: Power },
    ],
  },
];

export function Sidebar({ open, onClose }) {
  const { isSuperAdmin } = useAuth();

  return (
    <>
      {/*
       * The mobile backdrop. `aria-hidden` because the close affordance a
       * keyboard user needs is the button in the header, not this — a focusable
       * backdrop would be a tab stop with no label.
       */}
      {open ? (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-border bg-card',
          'transition-transform duration-200 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary">
              <ShieldCheck className="h-4 w-4 text-primary-foreground" aria-hidden="true" />
            </div>
            <span className="text-sm font-semibold tracking-tight">{APP_NAME}</span>
          </div>

          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <X />
          </Button>
        </div>

        <Separator />

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
          {SECTIONS.map((section, index) => {
            const items = section.items.filter((item) => !item.superAdmin || isSuperAdmin);
            if (!items.length) return null;

            return (
              <div key={section.label ?? index}>
                {section.label ? (
                  <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </p>
                ) : null}

                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        onClick={onClose}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'bg-primary/10 font-medium text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {item.label}
                            {/*
                             * Announced rather than only coloured — the active
                             * page is otherwise indicated by colour alone.
                             */}
                            {isActive ? <span className="sr-only">(current page)</span> : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <Separator />

        {/*
         * Stated where an operator will look for the button that is not there.
         * Withdrawals and income are read-only by design, and saying so in the
         * navigation saves the search that otherwise happens on the screen.
         */}
        <p className="px-4 py-3 text-[11px] leading-relaxed text-muted-foreground">
          Withdrawals and income ledgers are read-only. Payouts belong to the chain and the payout
          job.
        </p>
      </aside>
    </>
  );
}

export default Sidebar;
